import React from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { trackPhoneCall } from '@/lib/gtag';
import { servicesData } from '@/data/services';
import { Phone, CheckCircle } from 'lucide-react';

const Services = () => {
  const ServiceRow: React.FC<{ service: any; index: number }> = ({ service, index }) => {
    const [rowRef, visible] = useScrollAnimation(0.2);

    const imageFrom = index % 2 === 1 ? 'translate-x-8' : '-translate-x-8';
    const textFrom = index % 2 === 1 ? '-translate-x-8' : 'translate-x-8';

    return (
      <div ref={rowRef as any} className={`mb-20 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Image */}
          <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
            <div className={`relative flex items-center justify-center transition-all duration-700 w-full sm:w-11/12 lg:w-[70%] mx-auto group ${visible ? 'opacity-100 translate-x-0' : 'opacity-100 translate-x-0'
              }`}>
              <div className="w-64 h-64 rounded-full overflow-hidden shadow-xl ring-4 ring-[#2563eb]/20">
                <img
                  src={service.image}
                  srcSet={`/images/services/256${service.image.substring(service.image.lastIndexOf('/'))} 256w, /images/services/512${service.image.substring(service.image.lastIndexOf('/'))} 512w`}
                  sizes="(max-width: 640px) 256px, 512px"
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  style={{ objectPosition: service.imagePosition || 'center' }}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
            <div className={`space-y-4 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-100 translate-x-0'
              }`}>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {service.title}
                </h3>
                <p className="text-base text-[#2563eb] font-semibold mb-2">
                  {service.subtitle}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {service.description}
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="pt-2 flex flex-wrap gap-3">
                <a
                  href="tel:+351960488777"
                  className="bg-white hover:bg-gray-50 text-[#2563eb] font-semibold py-3 px-6 rounded-lg transition-colors duration-200 inline-flex items-center gap-2 border-2 border-[#2563eb]"
                  onClick={() => trackPhoneCall('+351960488777')}
                >
                  <Phone className="w-5 h-5" />
                  Pedir Orçamento
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Use data directly to ensure consistency
  const services = Object.values(servicesData);

  return (
    <section id="servicos" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Nossos Serviços
          </h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-6">
            Serviços profissionais de canalização em Lisboa — desentupimentos, reparações, instalações e manutenção
          </p>
        </div>

        {/* Services with Image and Text */}
        {services.map((service, index) => (
          <ServiceRow key={service.id} service={service} index={index} />
        ))}

        {/* Principais problemas que resolvemos */}
        <div className="mt-6 mb-16 relative overflow-hidden py-12 rounded-2xl">
          {/* Background animado com gradiente */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-green-50 to-blue-50 opacity-60 animate-pulse-slow"></div>

          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center animate-slide-up">
              Principais problemas que resolvemos
            </h3>
            <p className="text-gray-700 text-center mb-8 text-lg max-w-2xl mx-auto">
              Termos que as pessoas pesquisam quando têm problemas com canalização
            </p>

            <div className="flex flex-wrap justify-center gap-3 px-4">
              {[
                'desentupimento urgente',
                'canalização entupida',
                'sanita entupida',
                'ralo entupido',
                'fuga de água',
                'detecção de fugas',
                'reparar fugas de água',
                'canos partidos',
                'torneira avariada',
                'autoclismo não funciona',
                'canalizador 24h',
                'desentupimento Lisboa',
                'reparação canalização',
                'instalação canalização',
                'pinturas',
                'limpeza de vidros',
                'renovação WC'
              ].map((item, idx) => (
                <span
                  key={idx}
                  className="px-5 py-3 bg-gradient-to-r from-white to-gray-50 text-gray-800 rounded-full text-sm font-semibold border-2 border-gray-200 hover:border-[#2563eb] hover:bg-gradient-to-r hover:from-[#2563eb] hover:to-[#1e40af] hover:text-white transition-all duration-300 transform hover:scale-110 hover:shadow-lg cursor-pointer animate-slide-up hover:animate-float"
                  style={{
                    animationDelay: `${idx * 50}ms`,
                    animationFillMode: 'both'
                  }}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Services;